import React, { useState, useEffect } from 'react';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectBreakTaskEmployee } from '_redux/slices/timeline/selected-task';
import { selectSelectedTimeline } from '_redux/slices/timeline/timeline-areas';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import {
    createTask,
    saveEmployeeShift,
    selectCreateTaskStatus,
    selectCreateTaskError,
    clearCreateTaskStatus
} from '_redux/slices/timeline/timeline-data';
import { sendEmployeeNotification } from '_redux/slices/timeline/employees';

// custom components
import EmployeeProfilePicture from '../employee-components/employee-profile-picture';
import BreakInputForm from './break-input-form';
import CustomDivider from '_atoms/lines/custom-divider';

// icons
import { FiCoffee, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

// styles
import { Spacing, Colors } from '_styles';

// constants
import { EmployeeInformationColumnWidth } from '../timeline-constants';

const CreateBreakTask = (props) => {

    //#region redux
    const dispatch = useDispatch();
    const breakTaskEmployee = useSelector(selectBreakTaskEmployee);
    const selectedTimeline = useSelector(selectSelectedTimeline);
    const dbUser = useSelector(selectFirebaseInfo);
    const createTaskStatus = useSelector(selectCreateTaskStatus);
    const createTaskError = useSelector(selectCreateTaskError);
    //#endregion

    //#region local state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [pendingBreakDetails, setPendingBreakDetails] = useState(null);
    const [notificationSent, setNotificationSent] = useState(false);
    //#endregion

    // Send notification to employee about break assignment
    const sendBreakNotification = (breakStartTime) => {
        if (breakTaskEmployee?.FCMTokens) {
            const notification = {
                notificationTitle: "Break Assigned at " + breakStartTime,
                notificationText: `You have been assigned a break starting at ${breakStartTime}`,
                fcmToken: breakTaskEmployee?.FCMTokens,
                iata: dbUser?.details?.authority
            };
            dispatch(sendEmployeeNotification(notification));
        }
    };

    // Watch for status changes
    useEffect(() => {

        if (!isSubmitting) {
            //console.log('[BreakTask useEffect] Early return - not submitting');
            return;
        }

        if (createTaskStatus === 'succeeded') {

            setShowSuccess(true);
            setShowError(false);

            // Send notification to employee (only once)
            if (pendingBreakDetails?.breakStartTime && !notificationSent) {
                sendBreakNotification(pendingBreakDetails.breakStartTime);
                setNotificationSent(true);
            } else {
                //console.log('[BreakTask useEffect] Skipping notification - already sent or no break details');
            }

            // Close modal after 1.5 seconds
            const timer = setTimeout(() => {
                dispatch(clearCreateTaskStatus());
                setIsSubmitting(false);
                setShowSuccess(false);
                setPendingBreakDetails(null);
                setNotificationSent(false);
                props?.onClose?.();
            }, 1500);

            return () => {
                clearTimeout(timer);
            };
        }

        if (createTaskStatus === 'failed') {
            setShowError(true);
            setShowSuccess(false);
            setIsSubmitting(false);
            setPendingBreakDetails(null);
            setNotificationSent(false);
        }
    }, [createTaskStatus, isSubmitting, dispatch, props, notificationSent]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            dispatch(clearCreateTaskStatus());
        };
    }, [dispatch]);

    // Convert HH:mm into a Date anchored to today
    const createDateFromTime = (timeString, baseDate = new Date()) => {
        const [hours, minutes] = timeString.split(':').map(Number);
        const date = new Date(baseDate);
        date.setHours(hours, minutes, 0, 0);
        return date;
    };

    // Convert HH:mm into ISO date string anchored to today.
    // If referenceDate is given and this time falls at/before it, roll into tomorrow
    // (handles breaks that cross midnight, e.g. start 23:50 -> end 00:20).
    const createISOFromTime = (timeString, referenceDate = null) => {
        const [hours, minutes] = timeString.split(':').map(Number);
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        if (referenceDate && date <= referenceDate) {
            date.setDate(date.getDate() + 1);
        }
        return date;
    };

    // Round time string (HH:mm) down to nearest 30-minute interval
    const roundDownTo30Min = (timeString) => {
        const [hours, minutes] = timeString.split(':').map(Number);
        const roundedMinutes = Math.floor(minutes / 30) * 30;
        return `${String(hours).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`;
    };

    // Calculate offset in minutes from the 30-min placement time
    // e.g., 15:35 -> placementTime 15:30, offset 5
    // e.g., 16:15 -> placementTime 16:00, offset 15
    const calculatePlacementOffset = (timeString) => {
        const [, minutes] = timeString.split(':').map(Number);
        return minutes % 30; // Returns 0-29
    };

    // Create break task - writes to Firebase tasks collection
    const createBreakTask = (breakStartTime, breakEndTime, breakDuration) => {
        const now = new Date();
        const assignedTime = breakStartTime;
        const placementTime = roundDownTo30Min(breakStartTime); // For 30-min layout placement
        const placementOffset = calculatePlacementOffset(breakStartTime); // Offset in minutes for positioning

        let taskObject = {
            active: false,
            completed: false,
            employeeId: breakTaskEmployee?.employeeId,
            assignedTime: `${assignedTime?.split(":")?.[0]}:00`,
            placementTime: placementTime, // Rounded to nearest 30-min for timeline placement
            placementOffset: placementOffset, // Offset in minutes from placementTime (0-29) for precise positioning
            flightInformation: null,
            passengerInformation: null,
            flightBooking: false,
            allocatedTime: now,
            _id: `${breakTaskEmployee?.employeeId}-breakTask-${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}`,
            cancelled: false,
            pending: false,
            selectedTimeline: selectedTimeline?._id ?? null,
            dropOffLocation: null,
            isTransitBooking: false,
            notPlanned: false,
            isEstimatedBooking: false,
            passengerDutTime: null,
            combinedBooking: false,
            passengers: null,
            isBreakTask: true,
            breakTaskInformation: {
                breakTaskPeriod: breakDuration,
                breakTaskDate: now.toISOString().split("T")[0],
                employeeName: breakTaskEmployee?.title,
                airport: breakTaskEmployee?.airport,
                breakStartTime: createDateFromTime(breakStartTime),
                breakEndTime: (() => {
                    const start = createDateFromTime(breakStartTime);
                    const end = createDateFromTime(breakEndTime);
                    // If end time is before start time, the break crosses midnight — add a day
                    if (end <= start) {
                        end.setDate(end.getDate() + 1);
                    }
                    return end;
                })(),
            }
        };

        // DUB controllers expect new break tasks to transition directly into the active column
        if (dbUser?.details?.authority === "DUB") {
            taskObject = {
                ...taskObject,
                active: true,
                startTime: now,
            };
        }

        console.log('[Create Break Task] Creating task:', taskObject);
        dispatch(createTask(taskObject));
    };

    // COMMNETED OUT BECASUE I DONT UNDERSTAND WHAT THIS IS ACTUALLT DOING?
    // Save break to server endpoint
    const saveBreak = (breakStartTime, breakEndTime) => {
        const start = createISOFromTime(breakStartTime);
        const end = createISOFromTime(breakEndTime, start);
        const breakPayload = {
            userId: breakTaskEmployee?.userId,
            airport: breakTaskEmployee?.airport,
            employeeId: breakTaskEmployee?.employeeId,
            shift: {
                start: start.toISOString(),
                end: end.toISOString(),
            },
            isBreakTask: true,
            v2: true
        };

        dispatch(saveEmployeeShift(breakPayload));
    };

    const handleConfirm = ({ breakStartTime, breakEndTime, breakDuration }) => {
        setIsSubmitting(true);
        setShowError(false);
        setShowSuccess(false);

        // Store break details for notification
        setPendingBreakDetails({ breakStartTime, breakEndTime, breakDuration });

        // 1. Create task in Firebase
        createBreakTask(breakStartTime, breakEndTime, breakDuration);

        // 2. Save to server endpoint
        saveBreak(breakStartTime, breakEndTime);
    };

    const handleCancel = () => {
        props?.onClose?.();
    };

    return (
        <div style={{ padding: Spacing.SCALE_12 }}>

            {/* header */}
            <div style={{ marginBottom: Spacing.SCALE_22 }}>
                <FiCoffee size={28} color={Colors.PRM_LIGHT_BLUE} />
                <h5 style={{ fontWeight: 'bold', marginTop: '8px', marginBottom: '4px' }}>Create Break Task</h5>
                <p style={{ fontSize: '14px', color: 'grey', margin: 0 }}>
                    Assign a break to the selected employee during their shift
                </p>
            </div>

            <CustomDivider noMargin={true} />

            {/* employee profile picture */}
            {breakTaskEmployee && (
                <div className='mt-3'>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: 'grey', marginBottom: '6px', display: 'block' }}>Selected Employee</label>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: Spacing.SCALE_12,
                        width: EmployeeInformationColumnWidth,
                    }}>
                        <EmployeeProfilePicture
                            employee={breakTaskEmployee}
                            showName={true}
                            showRole={true}
                            name={breakTaskEmployee?.title}
                            role={breakTaskEmployee?.role}
                        />
                    </div>
                </div>
            )}

            {/* Status Messages */}
            {showSuccess && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    borderRadius: '6px',
                    marginTop: Spacing.SCALE_12,
                    marginBottom: Spacing.SCALE_12
                }}>
                    <FiCheckCircle size={20} color="#28a745" />
                    <span style={{ color: '#155724', fontWeight: '500' }}>Break task assigned successfully!</span>
                </div>
            )}

            {showError && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '6px',
                    marginTop: Spacing.SCALE_12,
                    marginBottom: Spacing.SCALE_12
                }}>
                    <FiAlertCircle size={20} color="#dc3545" />
                    <span style={{ color: '#721c24', fontWeight: '500' }}>
                        {createTaskError || 'Failed to assign break task. Please try again.'}
                    </span>
                </div>
            )}

            {/* break input */}
            <BreakInputForm
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                isSubmitting={isSubmitting}
                disabled={showSuccess}
            />

        </div>
    );
};

export default CreateBreakTask;
